import { useCallback } from "react";
import { LoaderOptions, useLoading } from "@context/LoadingContext";

interface UseGlobalLoaderReturn {
  showLoader: (options?: string | LoaderOptions) => void;
  hideLoader: () => void;
  setLoadingText: (text: string) => void;
  withLoader: <T extends any[], R>(
    asyncFunction: (...args: T) => Promise<R>,
    loadingOptions?: string | LoaderOptions
  ) => (...args: T) => Promise<R>;
}

export const useGlobalLoader = (): UseGlobalLoaderReturn => {
  const { showLoader, hideLoader, setLoadingText } = useLoading();

  const withLoader = useCallback(
    <T extends any[], R>(
      asyncFunction: (...args: T) => Promise<R>,
      loadingOptions?: string | LoaderOptions
    ) => {
      return async (...args: T): Promise<R> => {
        try {
          showLoader(loadingOptions);
          const result = await asyncFunction(...args);
          return result;
        } finally {
          hideLoader();
        }
      };
    },
    [showLoader, hideLoader]
  );

  return {
    showLoader,
    hideLoader,
    setLoadingText,
    withLoader,
  };
}; 